import React, { useState, useEffect } from 'react';
import { getVideos } from '../services/api';
import { ChevronLeft, ChevronRight, Play, FileText } from 'lucide-react';
import TranscriptModal from './TranscriptModal';
import './RecentVideosCarousel.css';

export default function RecentVideosCarousel() {
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const fetchRecentVideos = async () => {
    try {
      const data = await getVideos(3, 0);
      setVideos(data.videos);
    } catch (err) {
      console.error('Error fetching recent videos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentVideos();
  }, []);

  useEffect(() => {
    const hasPendingVideos = videos.some(
      v => v.status === 'queued' || v.status === 'rendering'
    );

    if (hasPendingVideos) {
      const interval = setInterval(fetchRecentVideos, 5000);
      return () => clearInterval(interval);
    }
  }, [videos]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setIsPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setIsPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPlaying(false);
  };

  const handlePlay = () => {
    const currentVideo = videos[currentIndex];
    if (currentVideo.status === 'completed' && currentVideo.video_url) {
      setIsPlaying(true);
    }
  };

  const handleTranscript = () => {
    setShowTranscript(true);
  };

  const handleCloseTranscript = () => {
    setShowTranscript(false);
  };

  const getLanguageFlag = (lang) => {
    switch (lang) {
      case 'fi': return '🇫🇮';
      case 'en': return '🇬🇧';
      case 'sv': return '🇸🇪';
      default: return '🌐';
    }
  };

  const getLanguageDisplay = (lang) => {
    switch (lang) {
      case 'fi': return 'Finnish';
      case 'en': return 'English';
      case 'sv': return 'Swedish';
      default: return lang.toUpperCase();
    }
  };

  const getQualityDisplay = (quality) => {
    switch (quality) {
      case 'qh': return '1080p';
      case 'qm': return '720p';
      case 'ql': return '480p';
      default: return quality;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { text: 'Ready', color: '#10b981', bg: '#d1fae5' };
      case 'rendering':
        return { text: 'Rendering', color: '#3b82f6', bg: '#dbeafe' };
      case 'queued':
        return { text: 'Queued', color: '#f59e0b', bg: '#fef3c7' };
      case 'failed':
        return { text: 'Failed', color: '#ef4444', bg: '#fee2e2' };
      default:
        return { text: 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  if (loading) {
    return (
      <div className="recent-carousel-loading">
        <div className="loading-spinner"></div>
        <p>Loading recent videos...</p>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="recent-carousel-empty">
        <div className="empty-icon">🎬</div>
        <h3>No videos yet</h3>
        <p>Create your first animation above to get started!</p>
      </div>
    );
  }

  const currentVideo = videos[currentIndex];
  const status = getStatusBadge(currentVideo.status);
  const isPlayable = currentVideo.status === 'completed' && currentVideo.video_url;

  return (
    <>
      <div className="recent-videos-carousel">
        <div className="recent-carousel-wrapper">
          {/* Navigation Buttons */}
          {videos.length > 1 && (
            <>
              <button 
                className="recent-nav-btn prev" 
                onClick={goToPrevious}
                aria-label="Previous video"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                className="recent-nav-btn next" 
                onClick={goToNext}
                aria-label="Next video"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Main Video Display */}
          <div className="recent-video-card">
            <div className="recent-video-container">
              {/* Show thumbnail or video player */}
              {!isPlaying || !currentVideo.video_url ? (
                <div 
                  className="recent-thumbnail"
                  onClick={handlePlay}
                  style={{ cursor: isPlayable ? 'pointer' : 'default' }}
                >
                  {currentVideo.thumbnail_url ? (
                    <img 
                      src={currentVideo.thumbnail_url} 
                      alt={currentVideo.scene_name}
                    />
                  ) : (
                    <div className="recent-thumbnail-placeholder">
                      <div className="placeholder-icon">🎬</div>
                      <p>No Preview</p>
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div 
                    className="recent-status-badge" 
                    style={{ 
                      backgroundColor: status.bg, 
                      color: status.color 
                    }}
                  >
                    {status.text}
                  </div>

                  {/* Play Overlay - Only for completed videos, visible on hover */}
                  {isPlayable && (
                    <div className="recent-play-overlay">
                      <div className="recent-play-button">
                        <Play size={48} fill="white" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="recent-video-player-wrapper">
                  <video
                    key={currentVideo.id}
                    controls
                    controlsList="nodownload"
                    autoPlay
                    className="recent-video-player"
                    onEnded={() => setIsPlaying(false)}
                  >
                    <source src={currentVideo.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>

            {/* Video Info */}
            <div className="recent-video-info">
              <div className="recent-video-header">
                <h3>{currentVideo.scene_name || 'Untitled Video'}</h3>                
              </div>
              <div className="recent-meta">
                <span className="recent-badge language">
                  <span className="badge-flag">{getLanguageFlag(currentVideo.language)}</span>
                  <span className="badge-text">{getLanguageDisplay(currentVideo.language)}</span>
                </span>
                <span className="recent-badge quality">
                  {getQualityDisplay(currentVideo.quality)}
                </span>
                <span className="recent-badge date">
                  {new Date(currentVideo.created_at).toLocaleDateString()}
                </span>
				{/* Transcript Button - Only show if transcript exists */}
                {currentVideo.transcript_markdown && (
                  <button 
                    className="recent-transcript-btn"
                    onClick={handleTranscript}
                    title="View transcript"
                  >
                    <FileText size={18} />
                    <span>Transcript</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Thumbnail Navigation */}
          {videos.length > 1 && (
            <div className="recent-thumbnails">
              {videos.map((video, index) => {
                const videoStatus = getStatusBadge(video.status);
                return (
                  <button
                    key={video.id}
                    className={`recent-thumbnail-btn ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`View ${video.scene_name}`}
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.scene_name} />
                    ) : (
                      <div className="thumb-placeholder">
                        🎬
                      </div>
                    )}
                    {/* Small status indicator */}
                    <div 
                      className="thumb-status"
                      style={{ backgroundColor: videoStatus.color }}
                    />
                    <div className="thumb-overlay">
                      <span className="thumb-title">{video.scene_name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Dot Indicators */}
          {videos.length > 1 && (
            <div className="recent-indicators">
              {videos.map((_, index) => (
                <button
                  key={index}
                  className={`recent-indicator ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to video ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Counter */}
          {videos.length > 1 && (
            <div className="recent-counter">
              <span className="current">{currentIndex + 1}</span>
              <span className="separator">/</span>
              <span className="total">{videos.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* Transcript Modal */}
      {showTranscript && currentVideo.transcript_markdown && (
        <TranscriptModal
          open={showTranscript}
          onClose={handleCloseTranscript}
          transcript={currentVideo.transcript_markdown}
        />
      )}
    </>
  );
}